// Estimate Settings (PROTOTYPE) — admin config that replaces the Excel config tabs.
// Route: /swiftboard/estimate-settings/:section
import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, NavLink, useParams } from "react-router-dom";
import { ArrowLeft, Info, Settings2 } from "lucide-react";
import Skeleton from "../../../../../components/ui/Skeleton";
import { useBreadcrumbLabels } from "../../../../../utils/breadcrumb-labels";
import { Card, Pill, Segmented } from "../../components/ui";
import { PrototypeBadge } from "../../components/EstimateBadges";
import { ErrorState } from "../../components/PageStates";
import { getConfig, listConfigVersions } from "../../mock/estimateService.mock";
import { SETTINGS_BREADCRUMBS, SETTINGS_SECTIONS } from "./estimate-settings/sections.config";

const ROLES = [
  { value: "ADMIN", label: "Admin" },
  { value: "ESTIMATOR", label: "Estimator" },
];

function SettingsSkeleton() {
  return (
    <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
      <Card className="flex flex-col gap-2 p-2">
        {SETTINGS_SECTIONS.map((s) => (
          <Skeleton key={s.slug} className="h-8 w-full" rounded="rounded-[4px]" />
        ))}
      </Card>
      <Card className="flex flex-col gap-3 p-4">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-3 w-72" />
        <div className="mt-2 grid gap-2.5 md:grid-cols-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" rounded="rounded-[4px]" />
          ))}
        </div>
      </Card>
    </div>
  );
}

export default function EstimateSettings() {
  const { section } = useParams();
  const [versions, setVersions] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [role, setRole] = useState("ADMIN");

  useBreadcrumbLabels(SETTINGS_BREADCRUMBS);

  // Edits go to the DRAFT version when one exists, else to a new draft based on ACTIVE.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const vs = await listConfigVersions();
      const target = vs.find((v) => v.status === "DRAFT") || vs.find((v) => v.status === "ACTIVE");
      const cfg = await getConfig(target?.id);
      setVersions(vs);
      setConfig(cfg);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const current = SETTINGS_SECTIONS.find((s) => s.slug === section);
  if (!current) return <Navigate to="/swiftboard/estimate-settings/global" replace />;

  const readOnly = role !== "ADMIN";
  const draft = versions?.find((v) => v.status === "DRAFT");
  const active = versions?.find((v) => v.status === "ACTIVE");
  const { Component } = current;
  const firstLoad = loading && !config;

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="mx-auto flex w-full max-w-[100rem] flex-col gap-4 py-3">
        {/* Top bar */}
        <Card className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[4px] bg-primary text-white">
              <Settings2 size={18} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h1 className="text-[16px] font-semibold text-text_primary">Estimate Settings</h1>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[12px] text-text_muted">
                {firstLoad ? (
                  <Skeleton className="h-4 w-56" />
                ) : draft ? (
                  <>
                    <span>Editing</span>
                    <Pill tone="warning" dot>
                      {draft.label} – Draft
                    </Pill>
                    <span>
                      unpublished · active is {active?.label}.{" "}
                      <Link to="/swiftboard/estimate-settings/versions" className="text-primary hover:underline">
                        Publish
                      </Link>
                    </span>
                  </>
                ) : active ? (
                  <>
                    <span>Viewing</span>
                    <Pill tone="success" dot>
                      {active.label} – Active
                    </Pill>
                    <span>Edits create a new draft version → Publish</span>
                  </>
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[11.5px] font-semibold text-text_secondary">Role (mock)</span>
              <Segmented options={ROLES} value={role} onChange={setRole} size="xs" ariaLabel="Mock role" />
            </div>
            <Link
              to="/swiftboard/estimates"
              className="inline-flex h-8 items-center gap-1.5 rounded-[4px] border border-border bg-surface px-3 text-[12.5px] font-medium text-text_primary focus:outline-none focus-visible:ring-2 focus-visible:ring-border_strong"
            >
              <ArrowLeft size={14} aria-hidden="true" /> Estimates
            </Link>
          </div>
        </Card>

        {readOnly && (
          <div className="flex items-center gap-2 rounded-[4px] border border-info_border bg-info_bg px-3 py-2 text-[12.5px] text-info" role="status">
            <Info size={14} aria-hidden="true" />
            Only Admin can edit estimate settings. You are viewing them read-only.
          </div>
        )}

        {firstLoad ? (
          <SettingsSkeleton />
        ) : error && !config ? (
          <ErrorState error={error} onRetry={load} title="Couldn't load estimate settings" />
        ) : (
          <div className="grid items-start gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
            <Card className="lg:sticky lg:top-0">
              <nav aria-label="Estimate settings sections" className="flex flex-col gap-0.5 p-1.5">
                {SETTINGS_SECTIONS.map((s) => (
                  <NavLink
                    key={s.slug}
                    to={`/swiftboard/estimate-settings/${s.slug}`}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 rounded-[4px] px-2.5 py-2 text-[12.5px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-border_strong ${
                        isActive ? "bg-primary_tint text-primary" : "text-text_secondary"
                      }`
                    }
                  >
                    <s.icon size={15} className="flex-shrink-0" aria-hidden="true" />
                    <span className="truncate">{s.label}</span>
                  </NavLink>
                ))}
              </nav>
            </Card>

            <div className="min-w-0">
              <Component key={`${config.versionId}-${current.slug}`} config={config} versions={versions} readOnly={readOnly} onSaved={load} />
            </div>
          </div>
        )}
      </div>
      <PrototypeBadge />
    </div>
  );
}
